// Types for the admin Reports feature. Must stay in sync with
// tracer/app/controllers/reports_controller.go response shapes.

/** One preset window the user can pick. `hours` drives the from/to the
 *  FE sends to the backend, which then picks the rollup tier + grain. */
export interface ReportPeriod {
  key: string; // e.g. "7d"
  label: string; // e.g. "7d"
  group: 'Hours' | 'Days' | 'Months';
  hours: number; // window length in hours
}

/** Backend tier/grain echoed back so the UI can label the x-axis. */
export type ReportGrain = 'hour' | 'day' | 'month';

export interface ReportSeriesPoint {
  bucket: string; // RFC3339 UTC
  requests: number;
  sessions: number;
  errors: number;
}

export interface ReportStatusSlice {
  status: string; // ok | warning | error | not_set
  count: number;
}

export interface ReportTotals {
  sessions: number;
  requests: number;
  /** Estimated (requests × ~4); messages are not rolled up. */
  messages_est: number;
  errors: number;
  error_rate: number; // 0..1
}

export interface ReportSummaryResponse {
  grain: ReportGrain;
  range: { from: string; to: string };
  totals: ReportTotals;
  series: ReportSeriesPoint[];
  status: ReportStatusSlice[];
}

export type ReportDimension = 'application' | 'consumer' | 'agency' | 'method' | 'provider';

export interface ReportBreakdownRow {
  key: string;
  requests: number;
  errors: number;
  error_rate: number; // 0..1
}

export interface ReportBreakdownResponse {
  dimension: ReportDimension;
  grain: ReportGrain;
  rows: ReportBreakdownRow[];
}

/** The full preset set requested for Reports. */
export const REPORT_PERIODS: ReportPeriod[] = [
  { key: '1h', label: '1h', group: 'Hours', hours: 1 },
  { key: '2h', label: '2h', group: 'Hours', hours: 2 },
  { key: '6h', label: '6h', group: 'Hours', hours: 6 },
  { key: '12h', label: '12h', group: 'Hours', hours: 12 },
  { key: '24h', label: '24h', group: 'Hours', hours: 24 },
  { key: '5d', label: '5d', group: 'Days', hours: 5 * 24 },
  { key: '7d', label: '7d', group: 'Days', hours: 7 * 24 },
  { key: '15d', label: '15d', group: 'Days', hours: 15 * 24 },
  { key: '30d', label: '30d', group: 'Days', hours: 30 * 24 },
  { key: '1mo', label: '1mo', group: 'Months', hours: 30 * 24 },
  { key: '3mo', label: '3mo', group: 'Months', hours: 91 * 24 },
  { key: '6mo', label: '6mo', group: 'Months', hours: 182 * 24 },
  { key: '13mo', label: '13mo', group: 'Months', hours: 396 * 24 },
];

export const REPORT_DIMENSIONS: { key: ReportDimension; label: string }[] = [
  { key: 'application', label: 'App' },
  { key: 'consumer', label: 'Consumer' },
  { key: 'agency', label: 'Agency' },
  { key: 'method', label: 'NDC method' },
  { key: 'provider', label: 'Provider' },
];

/** Display colours for outcome statuses (mirrors the mockup + FE pipes). */
export const STATUS_COLORS: Record<string, string> = {
  ok: '#16a34a',
  warning: '#f59e0b',
  error: '#dc2626',
  not_set: '#9ca3af',
};
